import { DateTime } from "luxon";
import { AgGrid } from "@jsq/ds/core";
import { DefaultColumnTypes } from "../../../../../utils/agGrid.constants";
import { ClientFilter } from "../../../../GridFilters/ClientFilter";
import { EntityFilter } from "../../../../GridFilters/EntityFilter";
import Money from "../../../../Money";
import { DistributionBatchRowItem } from "./types";

/**
 * List of the supported columns in the Distribution Batches table
 * (ordered from left to right)
 */
export enum SupportedColumns {
  Description = "description",
  DistributionDate = "distributionDate",
  ClientName = "clientName",
  EntityName = "entityName",
  PositionsCount = "positionsCount",
  DistributionAmount = "distributionAmount",
  RemainingToPayAmount = "remainingToPayAmount",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.Description, "Description"],
  [SupportedColumns.DistributionDate, "Distribution date"],
  [SupportedColumns.ClientName, "Client"],
  [SupportedColumns.EntityName, "Entity"],
  [SupportedColumns.PositionsCount, "Positions"],
  [SupportedColumns.DistributionAmount, "Distribution amount"],
  [SupportedColumns.RemainingToPayAmount, "Remaining to pay"],
]);

/**
 * AG Grid Column definitions for the Payment Batches table
 */
export const columnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.Description,
    headerName: columnNames.get(SupportedColumns.Description),
    pinned: "left",
    minWidth: 360,
  },
  {
    field: SupportedColumns.DistributionDate,
    headerName: columnNames.get(SupportedColumns.DistributionDate),
    valueFormatter: ({ data }: { data: DistributionBatchRowItem }) => {
      return data.distributionDate.toLocaleString(DateTime.DATE_MED);
    },
  },
  {
    field: SupportedColumns.ClientName,
    headerName: columnNames.get(SupportedColumns.ClientName),
    filter: ClientFilter,
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.EntityName,
    headerName: columnNames.get(SupportedColumns.EntityName),
    filter: EntityFilter,
    filterParams: {
      dependentFilter: SupportedColumns.ClientName,
    },
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.PositionsCount,
    headerName: columnNames.get(SupportedColumns.PositionsCount),
    maxWidth: 150,
  },
  {
    field: SupportedColumns.DistributionAmount,
    headerName: columnNames.get(SupportedColumns.DistributionAmount),
    type: DefaultColumnTypes.numericColumn,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: DistributionBatchRowItem }) => ({
      money: data.distributionAmount,
      rounded: false,
    }),
  },
  {
    field: SupportedColumns.RemainingToPayAmount,
    headerName: columnNames.get(SupportedColumns.RemainingToPayAmount),
    type: DefaultColumnTypes.numericColumn,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: DistributionBatchRowItem }) => ({
      money: data.remainingToPayAmount,
      rounded: false,
    }),
  },
];
